import { useCallback } from 'react';
import { usePrepareDocumentData } from './use-prepare-document-data';

const PDF_MAX_LOGO_WIDTH = 80;
const PDF_MAX_LOGO_HEIGHT = 60;

const UNSAFE_TAGS = [
  'style',
  'script',
  'noscript',
  'template',
  'svg',
  'head',
  'iframe',
  'object',
  'embed',
  'link'
];

function getCleanHTMLElement(html: string) {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  const elementsToRemove = tempDiv.querySelectorAll(UNSAFE_TAGS.join(', '));

  elementsToRemove.forEach((element) => element.remove());

  return tempDiv;
}

// Helper to extract plain text from HTML
function extractTextFromHTML(html: string): string {
  if (!html) {
    return '';
  }

  const tempDiv = getCleanHTMLElement(html);

  return tempDiv.textContent?.trim() || '';
}

// Helper to extract text lines from HTML preserving line breaks
function extractTextLinesFromHTML(html: string): string[] {
  if (!html) {
    return [];
  }

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  const lines: string[] = [];

  // Helper to process content of an element and extract lines
  const processElementContent = (element: HTMLElement): string[] => {
    const elementLines: string[] = [];
    let currentLine = '';

    // Process only direct child nodes (not recursive for nested divs)
    Array.from(element.childNodes).forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.trim();
        if (text) {
          currentLine += (currentLine ? ' ' : '') + text;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const childElement = node as HTMLElement;
        const tagName = childElement.tagName.toUpperCase();

        if (tagName === 'BR') {
          // <br> creates a line break
          if (currentLine.trim()) {
            elementLines.push(currentLine.trim());
            currentLine = '';
          }
        } else {
          // For other elements, just get their text content
          // (don't recurse into nested divs - they should be separate)
          const elementText = childElement.textContent?.trim();
          if (elementText) {
            currentLine += (currentLine ? ' ' : '') + elementText;
          }
        }
      }
    });

    // Add remaining line
    if (currentLine.trim()) {
      elementLines.push(currentLine.trim());
    }

    return elementLines;
  };

  // Process top-level nodes
  Array.from(tempDiv.childNodes).forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      if (text) {
        lines.push(text);
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      const tagName = element.tagName.toUpperCase();

      if (tagName !== 'BR') {
        const elementLines = processElementContent(element);
        lines.push(...elementLines);
      }
    }
  });

  return lines.filter((line) => line.length > 0);
}

// Load image and convert to base64 dataURL for pdfmake
async function loadImageAsDataURL(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error(`Failed to load image: ${url}`));
    };

    img.src = url;
  });
}

export function useDownloadDocument() {
  const prepareData = usePrepareDocumentData();

  return useCallback(async () => {
    try {
      // @ts-expect-error: pdfmake doesn't have types
      const pdfMake = (await import('pdfmake/build/pdfmake')).default;
      // @ts-expect-error: pdfFonts doesn't have types
      const pdfFonts = (await import('pdfmake/build/vfs_fonts')).default;
      pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.vfs;

      const data = prepareData();

      // Build PDF document definition
      const docDefinition = {
        pageSize: 'A4',
        pageMargins: [40, 60, 40, 60],
        defaultStyle: {
          font: 'Roboto',
          fontSize: 10,
          color: '#111111'
        },
        content: [] as any[],
        images: {} as { [key: string]: string }
      };

      // Add header with logo and company info on the same line
      if (data.logo || data.companyInfo) {
        const headerColumns: any[] = [];

        // Add logo if present
        if (data.logo) {
          try {
            const logoDataURL = await loadImageAsDataURL(data.logo.url);
            const logoKey = 'logo';
            docDefinition.images[logoKey] = logoDataURL;

            // Calculate logo size maintaining aspect ratio
            const aspectRatio = data.logo.width / data.logo.height;
            let logoWidth = PDF_MAX_LOGO_WIDTH;
            let logoHeight = PDF_MAX_LOGO_WIDTH / aspectRatio;

            if (logoHeight > PDF_MAX_LOGO_HEIGHT) {
              logoHeight = PDF_MAX_LOGO_HEIGHT;
              logoWidth = PDF_MAX_LOGO_HEIGHT * aspectRatio;
            }

            headerColumns.push({
              image: logoKey,
              width: logoWidth,
              height: logoHeight,
              alignment: 'left'
            });
          } catch (error) {
            console.warn('Failed to load logo image:', error);
          }
        } else {
          // Empty column if no logo
          headerColumns.push({ text: '', width: '*' });
        }

        // Add company info if present
        if (data.companyInfo) {
          // Use lines array if available, otherwise extract from HTML
          let companyInfoLines =
            data.companyInfo.lines.length > 0
              ? data.companyInfo.lines
              : extractTextLinesFromHTML(data.companyInfo.html);

          // In pdfmake, use stack to ensure each line is on a new line
          // Each item in stack is a separate line
          headerColumns.push({
            stack: companyInfoLines.map((line) => ({
              text: line,
              fontSize: 9,
              color: '#666666'
            })),
            alignment: 'right',
            width: '*'
          });
        } else {
          // Empty column if no company info
          headerColumns.push({ text: '', width: '*' });
        }

        docDefinition.content.push({
          columns: headerColumns,
          columnGap: 10,
          margin: [0, 0, 0, 16]
        });
      }

      // Add main title
      docDefinition.content.push({
        text: data.title,
        fontSize: 18,
        bold: true,
        color: '#111111',
        alignment: data.alignment || 'left',
        margin: [0, 16, 0, 0]
      });

      // Add subtitle if present
      if (data.subtitle) {
        const subtitleText = extractTextFromHTML(data.subtitle);
        docDefinition.content.push({
          text: subtitleText,
          fontSize: 10,
          color: '#666666',
          alignment: data.alignment || 'left',
          margin: [0, 8, 0, 0]
        });
      }

      // Add input data table
      if (data.inputDataRows.length > 0) {
        const inputTableBody = data.inputDataRows.map((row) => [
          {
            text: row.label,
            fontSize: 10,
            color: '#111111'
          },
          {
            text: row.value,
            fontSize: 10,
            color: '#111111',
            alignment: 'right'
          }
        ]);

        docDefinition.content.push({
          table: {
            widths: ['*', '*'],
            body: inputTableBody
          },
          layout: {
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 8,
            paddingBottom: () => 8,
            hLineWidth: () => 0.1,
            vLineWidth: () => 0,
            hLineColor: () => '#e0e0e0',
            vLineColor: () => '#e0e0e0'
          },
          margin: [0, 16, 0, 0]
        });
      }

      // Add results table
      if (data.resultsRows.length > 0) {
        // Add results heading
        docDefinition.content.push({
          text: data.resultsTitle,
          fontSize: 14,
          bold: true,
          color: '#111111',
          margin: [0, 32, 0, 0]
        });

        const resultsTableBody = data.resultsRows.map((row) => {
          const labelCell: any = {
            text: row.label,
            fontSize: 10,
            color: '#111111'
          };

          if (row.caption) {
            const captionText = extractTextFromHTML(row.caption);
            labelCell.stack = [
              { text: row.label, fontSize: 10, color: '#111111' },
              {
                text: captionText,
                fontSize: 8,
                color: '#666666',
                margin: [0, 4, 0, 0]
              }
            ];
          }

          const valueCell: any = {
            text: row.value,
            fontSize: row.isPrimary ? 12 : 10,
            bold: row.isPrimary,
            color: '#111111',
            alignment: 'right'
          };

          return [labelCell, valueCell];
        });

        docDefinition.content.push({
          table: {
            widths: ['*', '*'],
            body: resultsTableBody
          },
          layout: {
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 8,
            paddingBottom: () => 8,
            hLineWidth: () => 0.1,
            vLineWidth: () => 0,
            hLineColor: () => '#e0e0e0',
            vLineColor: () => '#e0e0e0'
          },
          margin: [0, 16, 0, 0]
        });
      }

      // Create and download PDF
      const pdfDocGenerator = pdfMake.createPdf(docDefinition);
      pdfDocGenerator.download(`calculation-results-${Date.now()}.pdf`);
    } catch (error) {
      console.error('PDF generation failed:', error);

      // Fallback: download HTML
      alert(
        'PDF generation failed. The document will be downloaded as HTML instead.'
      );

      const data = prepareData();
      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <title>${data.title}</title>
          </head>
          <body>
            <h1>${data.title}</h1>
            ${data.subtitle ? `<p>${data.subtitle}</p>` : ''}
            <p>Error generating PDF. Please try again.</p>
          </body>
        </html>
      `;

      const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `calculation-results-${Date.now()}.html`;
      link.click();
      URL.revokeObjectURL(url);
    }
  }, [prepareData]);
}
